import { useContext, useMemo } from 'react';

import { TrackUserEventActionDescription } from '@/hooks/useTrackUserEvent';
import { EntityDiagramContext } from '@/modules/entityDiagram/contexts/entityDiagram.context';
import { EntityDiagramVariant } from '@/modules/entityDiagram/types';
import { EntityMapContext } from '@/modules/entityMap/contexts/entityMap.context';
import { EstateWaterfallContext } from '@/modules/estateWaterfall/contexts/estateWaterfall.context';
import { AfterDeath, TileNotesInput } from '@/types/schema';

import { buildEntityDiagramTileNotesInput } from '../entityDiagramTileNotes.utils';
import { buildEntityMapTileNotesInput } from '../entityMapTileNotes.utils';
import {
  buildGroupTileNotesInputFromMembers,
  buildTileNotesInput,
} from '../TileNotesSummaryPanel.utils';

export interface BuildInputForGroupParams {
  nodeIds: string[];
  namespace: AfterDeath;
  groupDisplayName?: string;
}

export function combineGroupTileNotesInputs(
  inputs: TileNotesInput[],
  namespace: AfterDeath,
  groupDisplayName?: string
): TileNotesInput[] {
  if (!inputs.length) return [];
  return [
    {
      tileID: `group-${namespace}`,
      deathSection: namespace,
      entityIDs: Array.from(
        new Set(inputs.flatMap((input) => input.entityIDs ?? []))
      ),
      testamentaryEntityIDs: Array.from(
        new Set(inputs.flatMap((input) => input.testamentaryEntityIDs ?? []))
      ),
      groupDisplayName,
    },
  ];
}

export interface TileNotesGenerationTarget {
  trackingEvent: TrackUserEventActionDescription;
  trackingProps?: Record<string, string>;
  buildInputForTile: (tileId: string) => TileNotesInput[];
  buildInputForGroup: (params: BuildInputForGroupParams) => TileNotesInput[];
  /**
   * When true, the generate button should only be rendered for tiles that
   * resolve to a non-empty input. The waterfall keeps its historical
   * always-show behavior; entity diagrams hide the button for tiles that
   * can't generate notes (individuals, organizations, unsupported kinds).
   */
  hideWhenNoInput: boolean;
}

/**
 * Resolves which diagram surface the tile-notes field is mounted in. Returns
 * a waterfall-backed target under an EstateWaterfallProvider, an
 * entity-diagram-backed target under an EntityDiagramProvider, an
 * entity-map-backed target under an EntityMapProvider, and null anywhere else,
 * where AI notes generation is unavailable.
 */
export function useTileNotesGenerationTarget(): TileNotesGenerationTarget | null {
  // All three contexts have non-null default values, so plain useContext is
  // safe outside their providers; the graphs are only populated inside one.
  const { state: waterfallState } = useContext(EstateWaterfallContext);
  const { state: entityDiagramState } = useContext(EntityDiagramContext);
  const { state: entityMapState } = useContext(EntityMapContext);

  const waterfallGraph = waterfallState.waterfallGraph;
  const entityDiagramGraph = entityDiagramState.entityDiagramGraph;
  const entityDiagramVariant = entityDiagramState.entityDiagramVariant;
  const entityMapGraph = entityMapState.entityGraph;

  return useMemo(() => {
    if (waterfallGraph) {
      return {
        trackingEvent: 'waterfall generate_notes_clicked' as const,
        buildInputForTile: (tileId: string) =>
          buildTileNotesInput([tileId], waterfallGraph),
        buildInputForGroup: ({
          nodeIds,
          namespace,
          groupDisplayName,
        }: BuildInputForGroupParams) => {
          const groupInput = buildGroupTileNotesInputFromMembers({
            tileIds: nodeIds,
            namespace,
            waterfallGraph,
            groupDisplayName,
          });
          return groupInput ? [groupInput] : [];
        },
        hideWhenNoInput: false,
      };
    }

    if (entityDiagramGraph) {
      return {
        trackingEvent: 'entity_diagram generate_notes_clicked' as const,
        trackingProps: { diagramVariant: entityDiagramVariant },
        buildInputForTile: (tileId: string) =>
          buildEntityDiagramTileNotesInput(
            [tileId],
            entityDiagramGraph,
            entityDiagramVariant
          ),
        buildInputForGroup: ({
          nodeIds,
          namespace,
          groupDisplayName,
        }: BuildInputForGroupParams) =>
          combineGroupTileNotesInputs(
            buildEntityDiagramTileNotesInput(
              nodeIds.map((id) => `${namespace}:${id}`),
              entityDiagramGraph,
              entityDiagramVariant
            ),
            entityDiagramVariant === EntityDiagramVariant.Dispositions
              ? namespace
              : AfterDeath.None,
            groupDisplayName
          ),
        hideWhenNoInput: true,
      };
    }

    if (entityMapGraph) {
      return {
        trackingEvent: 'entity_map generate_notes_clicked' as const,
        buildInputForTile: (tileId: string) =>
          buildEntityMapTileNotesInput([tileId], entityMapGraph),
        buildInputForGroup: ({
          nodeIds,
          groupDisplayName,
        }: BuildInputForGroupParams) =>
          combineGroupTileNotesInputs(
            buildEntityMapTileNotesInput(nodeIds, entityMapGraph),
            AfterDeath.None,
            groupDisplayName
          ),
        hideWhenNoInput: true,
      };
    }

    return null;
  }, [
    waterfallGraph,
    entityDiagramGraph,
    entityDiagramVariant,
    entityMapGraph,
  ]);
}
